import { useCallback, useEffect, useMemo, useState } from "react";

import { WinTicketContentFooter } from "./win-ticket-content-footer";
import { Ticket as TicketModel } from "../../../../../models";
import {
  ReplaceVirtualTranslationKey,
  truncWithPrecision,
} from "../../../../../helpers";
import { BetTypeNames } from "../../../../../constants";
import { BetInfo } from "../ticket/bet-info";
import { Market } from "../ticket/market";
import { Ticket } from "../ticket";
import { useTranslation } from "../../../../../hooks";
import { TicketConfig } from "../../../../../models/partnerResponse";

interface WinTicketProps {
  data: TicketModel;
  isExpress: boolean;
  ticketConfig: TicketConfig;
}

export const WinTicket: React.FC<WinTicketProps> = ({
  data,
  isExpress,
  ticketConfig,
}) => {
  const t = useTranslation();
  const {
    barcode,
    ticketInfos,
    winAmount,
    amount,
    paymentDate,
    betType,
    ticketId,
    userName,
  } = data;

  const [ticketLoaded, setTicketLoaded] = useState(false);
  const [qrImgLoaded, setQrImgLoaded] = useState(false);
  const [imgImageError, setImgImageError] = useState<any>(false);

  useEffect(() => {
    if (+barcode && !qrImgLoaded) return;

    if (ticketLoaded || imgImageError || ticketConfig?.logo === null) {
      window.print();
    }
  }, [ticketLoaded, imgImageError, qrImgLoaded]);

  const firstMarketData = useMemo(() => {
    const [firstMarket] = ticketInfos;
    return firstMarket;
  }, [ticketInfos]);

  const currencyCode = firstMarketData?.currency || "";

  const handleTicketLoad = useCallback(() => {
    setTicketLoaded(ticketConfig?.showLogo || true);
  }, []);

  const handleQrImgLoad = useCallback(() => {
    setQrImgLoaded(ticketConfig?.showQR || true);
  }, []);

  const totalOdd = useMemo(() => {
    const odd = ticketInfos.reduce<number>((acc, market) => {
      return acc * market.odds;
    }, 1);
    return truncWithPrecision(odd);
  }, [ticketInfos]);

  const betTypeName = useMemo(() => {
    return isExpress
      ? `${BetTypeNames[betType]} (${ticketInfos.length})`
      : BetTypeNames[betType];
  }, [isExpress, ticketInfos]);

  return (
    <Ticket
      barcode={barcode}
      onTicketLoad={handleTicketLoad}
      showLogo={ticketConfig?.showLogo}
      logo={ticketConfig?.logo}
      setImgImageError={setImgImageError}
      handleQrImgLoad={handleQrImgLoad}
    >
      <BetInfo
        barCode={barcode}
        date={paymentDate}
        ticketId={ticketId}
        userName={userName}
        betTypeName={betTypeName}
        showUserName={ticketConfig?.showUserName}
      />
      {ticketInfos.map(
        (
          {
            betAmount,
            odds,
            marketName,
            currency,
            winAmount,
            gameName,
            translationSymbols,
            roundId,
            eventName,
            numbers,
            combinations,
            possibleCombinations,
          },
          index,
        ) => (
          <Market
            odd={odds}
            showWinAmount
            combinations={combinations}
            betType={betType}
            key={marketName}
            roundId={roundId}
            gameName={gameName}
            isExpress={isExpress}
            winAmount={winAmount}
            betAmount={betAmount}
            possibleCombinations={possibleCombinations}
            eventName={
              translationSymbols?.homeTeam
                ? `${ReplaceVirtualTranslationKey(
                    t(translationSymbols?.homeTeam),
                    translationSymbols,
                    t,
                  )} - ${ReplaceVirtualTranslationKey(
                    t(translationSymbols?.awayTeam),
                    translationSymbols,
                    t,
                  )}`
                : ""
            }
            marketName={
              numbers ||
              ReplaceVirtualTranslationKey(
                t(translationSymbols?.marketName || marketName),
                translationSymbols,
                t,
              )
            }
            selectionName={ReplaceVirtualTranslationKey(
              t(translationSymbols?.selectionName),
              translationSymbols,
              t,
            )}
            currencyCode={currency}
            showLine={index !== ticketInfos.length - 1}
            ticketConfig={ticketConfig}
          />
        ),
      )}
      <WinTicketContentFooter
        totalOdd={totalOdd}
        totalWin={winAmount}
        isExpress={isExpress}
        totalBetAmount={amount}
        currencyCode={currencyCode}
        showTotalWinAmount={
          ticketConfig?.showExpectedMaxWin || ticketConfig?.shortTicket
        }
      />
    </Ticket>
  );
};
